import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Hexagon, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/packages", label: "Packages" },
  { to: "/process", label: "Our Process" },
  { to: "/refer-earn", label: "Refer & Earn" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dash = user ? (user.role === "admin" ? "/admin" : user.role === "partner" ? "/partner" : "/client") : null;

  return (
    <header className={`sticky top-0 z-50 transition-all ${scrolled ? "glass-strong" : "bg-transparent"} border-b ${scrolled ? "border-white/10" : "border-transparent"}`}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-18 py-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
            <span className="relative grid place-items-center w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 btn-glow">
              <Hexagon className="w-5 h-5 text-white" strokeWidth={2.4} />
            </span>
            <div className="leading-none">
              <div className="font-display font-bold text-white text-lg tracking-tight">Nova</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-cyan-300/80">Web Solutions</div>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "text-white bg-white/5" : "text-slate-300 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                <Button data-testid="nav-dashboard-btn" variant="ghost" className="text-slate-200 hover:text-white hover:bg-white/5" onClick={() => navigate(dash)}>
                  Dashboard
                </Button>
                <Button data-testid="nav-logout-btn" variant="outline" className="border-white/15 bg-transparent text-white hover:bg-white/10" onClick={async () => { await logout(); navigate("/"); }}>
                  Log out
                </Button>
              </>
            ) : (
              <div className="relative" onMouseLeave={() => setLoginOpen(false)}>
                <button data-testid="nav-login-menu" onMouseEnter={() => setLoginOpen(true)} onClick={() => setLoginOpen((v) => !v)}
                  className="flex items-center gap-1 px-3.5 py-2 rounded-lg text-sm font-medium text-slate-200 hover:text-white hover:bg-white/5">
                  Login <ChevronDown className="w-4 h-4" />
                </button>
                {loginOpen && (
                  <div className="absolute right-0 mt-1 w-52 glass-strong rounded-xl p-1.5 shadow-2xl">
                    <Link data-testid="nav-client-login" to="/login?role=client" className="block px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-white/10 hover:text-white">Client Login</Link>
                    <Link data-testid="nav-partner-login" to="/login?role=partner" className="block px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-white/10 hover:text-white">Partner Login</Link>
                    <Link data-testid="nav-admin-login" to="/admin/login" className="block px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-white/10 hover:text-white">Admin Login</Link>
                    <div className="h-px bg-white/10 my-1" />
                    <Link data-testid="nav-partner-signup" to="/partner/register" className="block px-3 py-2 rounded-lg text-sm text-cyan-300 hover:bg-white/10">Become a Partner</Link>
                  </div>
                )}
              </div>
            )}
            <Button data-testid="nav-request-demo-button" className="bg-violet-600 hover:bg-violet-500 text-white font-semibold btn-glow" onClick={() => navigate("/contact")}>
              Request Free Demo
            </Button>
          </div>

          <button data-testid="nav-mobile-toggle" className="lg:hidden text-white p-2" onClick={() => setOpen((v) => !v)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden glass-strong border-t border-white/10 px-4 py-4 space-y-1">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
              data-testid={`nav-mobile-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className="block px-3 py-2.5 rounded-lg text-slate-200 hover:bg-white/10 hover:text-white">{n.label}</Link>
          ))}
          <div className="h-px bg-white/10 my-2" />
          <Link to="/login?role=client" onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-lg text-slate-200 hover:bg-white/10">Client Login</Link>
          <Link to="/login?role=partner" onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-lg text-slate-200 hover:bg-white/10">Partner Login</Link>
          <Link to="/admin/login" onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-lg text-slate-200 hover:bg-white/10">Admin Login</Link>
          <Button className="w-full mt-2 bg-violet-600 hover:bg-violet-500" onClick={() => { setOpen(false); navigate("/contact"); }}>Request Free Demo</Button>
        </div>
      )}
    </header>
  );
}
